// Measurement grid helpers.
// Values stored in the sizing store are keyed "measure|size" (canonical, used
// for API/database).  The UI editors (wizard + edit drawer) key them as
// "ri-ci" (row index / column index) so the grid can drag-reorder without
// name lookups.  These helpers convert between the two + power smart-fill,
// clipboard paste, drag-reorder remap and axis-value counts.

export const gridToValueMap = (values, sizes, measures) => {
    const out = {};
    sizes.forEach((sz, ri) => measures.forEach((m, ci) => {
        const v = values[`${ri}-${ci}`];
        if (v !== undefined && v !== "") out[`${m}|${sz}`] = String(v);
    }));
    return out;
};

export const valueMapToGrid = (map, sizes, measures) => {
    const out = {};
    sizes.forEach((sz, ri) => measures.forEach((m, ci) => {
        const v = map?.[`${m}|${sz}`];
        if (v !== undefined && v !== "") out[`${ri}-${ci}`] = String(v);
    }));
    return out;
};

// Detect per-measure step from the first two filled cells and fill the blanks
export const gridSmartFill = (values, rowCount, colCount) => {
    const next = { ...values };
    let filled = 0;
    for (let ci = 0; ci < colCount; ci++) {
        const pts = [];
        for (let ri = 0; ri < rowCount; ri++) {
            const v = parseFloat(values[`${ri}-${ci}`]);
            if (!isNaN(v)) pts.push([ri, v]);
        }
        if (pts.length < 2) continue;
        const [r1, v1] = pts[0];
        const [r2, v2] = pts[1];
        if (r2 === r1) continue;
        const step = (v2 - v1) / (r2 - r1);
        for (let ri = 0; ri < rowCount; ri++) {
            const key = `${ri}-${ci}`;
            if (next[key] === undefined || next[key] === "") {
                const val = v1 + (ri - r1) * step;
                next[key] = Number.isInteger(val) ? String(val) : String(Math.round(val * 10) / 10);
                filled++;
            }
        }
    }
    return { next, filled };
};

// Paste an Excel/CSV block starting at the focused cell
export const gridPaste = (values, text, startRi, startCi, rowCount, colCount) => {
    const rows = text.replace(/\r/g, "").split("\n").filter((r) => r.trim() !== "");
    const next = { ...values };
    let filled = 0;
    rows.forEach((row, dr) => {
        row.split(/\t|;/).forEach((cell, dc) => {
            const ri = startRi + dr;
            const ci = startCi + dc;
            if (ri >= rowCount || ci >= colCount) return;
            const clean = cell.trim().replace(",", ".");
            if (clean === "" || isNaN(parseFloat(clean))) return;
            next[`${ri}-${ci}`] = clean;
            filled++;
        });
    });
    return { next, filled };
};

// Remap the `values` grid ({ "ri-ci": cm }) after a row (size) or column
// (measure) is reordered.  Callers pass the old array + new array;  we
// build an old-idx → new-idx map from that and rewrite the grid keys.
export const remapValuesAfterReorder = (values, oldArr, newArr, axis) => {
    // axis = "row" reorders sizes (ri), "col" reorders measures (ci)
    const idxMap = new Map();
    oldArr.forEach((k, oldIdx) => {
        const newIdx = newArr.indexOf(k);
        idxMap.set(oldIdx, newIdx);
    });
    const next = {};
    Object.entries(values).forEach(([key, v]) => {
        const [ri, ci] = key.split("-").map(Number);
        const newRi = axis === "row" ? (idxMap.get(ri) ?? ri) : ri;
        const newCi = axis === "col" ? (idxMap.get(ci) ?? ci) : ci;
        next[`${newRi}-${newCi}`] = v;
    });
    return next;
};

// Count non-empty cm values in a single row (size) or column (measure).
// Used to warn the merchant before deleting: "N cm değeri kaybolacak".
export const countAxisValues = (values, axisIdx, axis, otherLen) => {
    let n = 0;
    for (let i = 0; i < otherLen; i++) {
        const key = axis === "row" ? `${axisIdx}-${i}` : `${i}-${axisIdx}`;
        const v = values[key];
        if (v !== undefined && v !== "" && v !== null) n++;
    }
    return n;
};
